import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { ART_STYLE } from '../../../../theme/tokens';
import { LumioGlyph } from '../../../creatives/components/LumioMark';
import { MoodBoardField } from '../../../creatives/components/StoryboardComposer/MoodBoardField';
import { MAX_MASCOT_IMAGES, type ArtStyleMascot } from '../../art-style-api';
import { useTypedReveal } from '../../useTypedReveal';
import { MascotAvatar } from '../MascotAvatar';
import { Card, CardHead, IconAction, NameInput } from './MascotsField.styles';

export type MascotCardProps = {
  mascot: ArtStyleMascot;
  /** Its images still uploading. */
  pending: { key: string; dataUri: string }[];
  /** Lumio is looking at it right now. */
  describing: boolean;
  /** Bumps when Lumio's suggested name lands (types it in); 0 never animates. */
  nameReveal: number;
  onRename: (name: string) => void;
  onSuggestName: () => void;
  onRemove: () => void;
  onAddFiles: (files: File[]) => void;
  onRemoveImage: (index: number) => void;
  /** Open its image at this index full size (left out, the images are only pictures). */
  onOpenImage?: (index: number) => void;
};

/** Longest mascot name the field accepts (Lumio suggests one or two words). */
const MAX_NAME = 40;

/**
 * One mascot of the cast: its avatar (a round crop of its first image), its name (editable in place; Lumio
 * suggests one once it has an image, typed in as it lands, and "Suggest a name" asks again), its images (up to
 * four of the same character) and Remove.
 */
export const MascotCard = ({
  mascot,
  pending,
  describing,
  nameReveal,
  onRename,
  onSuggestName,
  onRemove,
  onAddFiles,
  onRemoveImage,
  onOpenImage,
}: MascotCardProps): React.ReactElement => {
  const { t } = useTranslation();
  const shown = useTypedReveal(mascot.name.length, nameReveal);
  const revealing = shown !== null;
  const hasImages = mascot.images.length > 0;
  const label = mascot.name.trim() || t('Unnamed character');

  let placeholder = t('Name your character');
  if (describing) placeholder = t('Lumio is naming it…');
  else if (!hasImages) placeholder = t('Add an image and Lumio names it');

  return (
    <Card>
      <CardHead>
        <MascotAvatar url={mascot.images[0]?.url} size={ART_STYLE.MASCOT_AVATAR_PX} />
        <NameInput
          value={revealing ? mascot.name.slice(0, shown) : mascot.name}
          readOnly={revealing}
          maxLength={MAX_NAME}
          placeholder={placeholder}
          aria-label={t('Character name')}
          $revealing={revealing}
          onChange={(event) => onRename(event.target.value)}
        />
        <IconAction
          type="button"
          $working={describing}
          disabled={!hasImages || describing}
          title={hasImages ? t('Suggest a name') : t('Add an image first.')}
          aria-label={t('Suggest a name for {{name}}', { name: label })}
          onClick={() => {
            if (hasImages && !describing) onSuggestName();
          }}
        >
          <LumioGlyph size={14} />
        </IconAction>
        <IconAction
          type="button"
          title={t('Remove character')}
          aria-label={t('Remove {{name}}', { name: label })}
          onClick={onRemove}
        >
          <Icon name="trash" size={15} />
        </IconAction>
      </CardHead>
      <MoodBoardField
        headless
        images={mascot.images.map((image) => image.url)}
        pending={pending}
        max={MAX_MASCOT_IMAGES}
        removeLabel={t('Remove character image')}
        addTitle={t('Add character image')}
        onAddFiles={onAddFiles}
        onRemove={onRemoveImage}
        onOpen={onOpenImage}
        openLabel={t('Open character image')}
      />
    </Card>
  );
};

export default MascotCard;
